import React, { useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import Modal from "../Modal/Modal";
import {
  Typography,
  Button,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio
} from "@mui/material";
import { Tooltip } from "../Tooltip";
import IltVersionInstructorTooltipContent from "../Tooltip/IltVersionInstructorTooltipContent";
import { formatDate } from "../../utils/datetime";
import {
  displayJoinButton,
  iltScheduledAt,
  isIlt
} from "../../utils/course_utils";
import ButtonGroup from "../Buttons/ButtonGroup";
import { courseTypes, assignmentStatuses } from "../../constants";

const SubmitButton = ({ courseType, onClick, version, hasUnmetPrereqs }) => {
  const { t } = useTranslation();
  const showEnrollAndJoinBtn =
    version &&
    !hasUnmetPrereqs &&
    displayJoinButton(version.zonedStartDate, version.zonedEndDate);
  // Direct user immediately into course if enroll & join is clicked or if course is Online
  const enrollAndJoin =
    showEnrollAndJoinBtn ||
    courseType === courseTypes.online.toLowerCase() ||
    courseType === courseTypes.policy.toLowerCase();
  return (
    <Button
      color="primary"
      variant="contained"
      onClick={() => onClick(enrollAndJoin)}
    >
      {showEnrollAndJoinBtn
        ? t("courses.chooseVersionModal.enrollAndJoin")
        : t("courses.chooseVersionModal.continue", {
            context: courseType
          })}
    </Button>
  );
};

export default function ChooseVersionModalDialog(props) {
  const [selectedReleaseId, setSelectedReleaseId] = useState(null);
  const { t } = useTranslation();
  const {
    user,
    courseVersions,
    assignment,
    showDialog,
    close,
    enrollIntoCourse,
    unenrollFromVersion,
    setChangeEnrollmentModal
  } = props;
  const selectedVersion = courseVersions?.find(
    version => version.releaseId == selectedReleaseId
  );
  const handleChangeValue = event => {
    setSelectedReleaseId(event.target.value);
  };
  const hasBrokenEnrollment =
    assignment?.status === assignmentStatuses.needsUnenroll;

  const handleContinueClicked = enrollAndJoin => {
    const version = courseVersions.find(
      version => parseInt(version.releaseId) === parseInt(selectedReleaseId)
    );
    if (version) {
      if (assignment?.enrolledTrainingObjectId && !hasBrokenEnrollment) {
        setChangeEnrollmentModal(assignment.versions, version);
      } else if (enrollIntoCourse) {
        const navigation = enrollAndJoin
          ? version.showOverview
            ? "overview"
            : "first"
          : false;
        enrollIntoCourse(version, assignment, navigation);
        close();
      }
    }
  };

  const getLocation = (location, courseType) => {
    const type = courseType ? courseType.toLowerCase() : null;
    if (type === courseTypes.webinar.toLowerCase()) {
      return t("courseCard.courseType.webinar");
    } else if (type === courseTypes.virtual.toLowerCase()) {
      return t("courseCard.courseType.virtualClassroom");
    } else if (location && type === courseTypes.ilt.toLowerCase()) {
      return `${location.city}, ${location.state}`;
    } else {
      return null;
    }
  };

  return (
    <Modal
      data-qa="choose-version-modal-dialog"
      dialogTitle={
        <Typography variant="h4" color="textPrimary">
          {assignment &&
            t(
              `courses.chooseVersionModal.title.${
                isIlt(assignment) ? "ilt" : "online"
              }`
            )}
          :
        </Typography>
      }
      dialogContent={
        courseVersions && courseVersions.length > 0 ? (
          <FormControl component="fieldset">
            <RadioGroup
              aria-label="CourseVersions"
              name="courseVersions"
              value={selectedReleaseId}
              onChange={event => handleChangeValue(event)}
            >
              {courseVersions &&
                courseVersions.map(courseVersion => {
                  let label = courseVersion.name;
                  if (isIlt(assignment)) {
                    const endDateTime =
                      courseVersion.endDate &&
                      courseVersion.endDate !== courseVersion.scheduledDate
                        ? `${formatDate(courseVersion.endDate)} ${
                            courseVersion.endTime
                          }`
                        : `${
                            courseVersion.endTime ? courseVersion.endTime : ""
                          }`;
                    const scheduledDateAndTime =
                      `${formatDate(new Date(courseVersion.scheduledDate))} ` +
                      (endDateTime
                        ? `${courseVersion.scheduledTime} - ${endDateTime}`
                        : `${iltScheduledAt(courseVersion.scheduledTime)}`);
                    label += ": " + scheduledDateAndTime;
                  }

                  if (courseVersion.iltInfo) {
                    const location = getLocation(
                      courseVersion.iltInfo.trainingLocation,
                      courseVersion.typeName
                    );
                    if (location) {
                      label += " - " + location;
                    }
                    if (
                      courseVersion.id == assignment.enrolledTrainingObjectId
                    ) {
                      label += " (Enrolled)";
                    }
                  }
                  return user &&
                    courseVersion.iltInfo &&
                    courseVersion.iltInfo.instructor &&
                    courseVersion.iltInfo.instructor.id === user.id ? (
                    <Tooltip
                      title={<IltVersionInstructorTooltipContent />}
                      key={courseVersion.releaseId}
                    >
                      <FormControlLabel
                        key={courseVersion.releaseId}
                        value={String(courseVersion.releaseId)}
                        control={<Radio color="primary" />}
                        label={label}
                        disabled={true}
                      />
                    </Tooltip>
                  ) : (
                    <FormControlLabel
                      key={courseVersion.releaseId}
                      value={String(courseVersion.releaseId)}
                      control={<Radio color="primary" />}
                      label={label}
                    />
                  );
                })}
            </RadioGroup>
          </FormControl>
        ) : (
          <Typography>
            {assignment &&
              t(
                `courses.chooseVersionModal.noActiveVersions.${
                  isIlt(assignment) ? "ilt" : "online"
                }`
              )}
          </Typography>
        )
      }
      dialogActions={
        <ButtonGroup>
          <Button color="primary" onClick={close}>
            {t("closeBtn")}
          </Button>
          {assignment?.enrolledCtoaId && (
            <Button
              color="primary"
              onClick={() =>
                unenrollFromVersion(assignment, () => {
                  close();
                })
              }
              disabled={assignment.enrollmentStatus !== "self"}
            >
              {t("unenrollBtn")}
            </Button>
          )}
          {courseVersions?.length > 0 && (
            <SubmitButton
              courseType={assignment.courseType?.toLowerCase()}
              onClick={handleContinueClicked}
              version={selectedVersion}
              hasUnmetPrereqs={!!assignment.prerequisites?.length}
            />
          )}
        </ButtonGroup>
      }
      showCloseIcon
      open={showDialog}
      onClose={close}
      maxWidth="md"
      scrollable
    />
  );
}

ChooseVersionModalDialog.propTypes = {
  user: PropTypes.any,
  courseVersions: PropTypes.array,
  assignment: PropTypes.any,
  showDialog: PropTypes.bool,
  close: PropTypes.func,
  enrollIntoCourse: PropTypes.func,
  setChangeEnrollmentModal: PropTypes.func,
  unenrollFromVersion: PropTypes.func
};
